// lexiqamus_site_page_form_controller.js
import { Controller } from 'stimulus'
import {
  TINYMCE_SELF_HOSTED_BASE_URL,
  TINYMCE_BODY_TEXT_COLOR,
  TINYMCE_TABLE_CELL_BORDER,
  TINYMCE_BLOCKQUOTE_BORDER,
  TINYMCE_NON_EDITABLE_BG,
  TINYMCE_CMS_EMBEDDED_STYLE_ATTR,
  TINYMCE_CMS_EMBEDDED_STYLE_VALUE,
} from '../lib/lexiqamus_tinymce_config'

// BuildLQ LexiQamus CMS page form: self-hosted TinyMCE rich text, hero image via Active Storage media library.
// mediaMode tracks 'editor' (insert into active TinyMCE tab) vs 'hero' (set hero image for a language tab).
// heroLanguageId stores which language tab triggered the hero picker so the right field is updated.
export default class extends Controller {
  static targets = [
    'mediaModal',
    'previewModal',
    'previewImage',
    'previewCaption',
    'libraryContainer',
    'spinner',
  ]

  static values = {
    uploadUrl:            String, // POST /lexiqamus_site_pages/upload_image
    loadImageDataUrl:     String, // GET  /lexiqamus_site_pages/load_image_data
    removeMediaUrl:       String, // DELETE /lexiqamus_site_pages/media/:id (MEDIA_ID placeholder)
    undefinedHeroImageUrl: String, // SVG placeholder shown when hero image is cleared
  }

  // Wires form submit sync, loads self-hosted TinyMCE, and binds the media modal reload trigger.
  connect() {
    this.mediaMode      = 'editor'
    this.heroLanguageId = null
    this.mediaModalInstance   = null
    this.previewModalInstance = null
    this.formElement = this.element.querySelector('.js-site-page-form-inner')

    if (this.formElement) {
      this.syncEditorsBeforeSubmit = this.syncEditorsBeforeSubmit.bind(this)
      this.formElement.addEventListener('submit', this.syncEditorsBeforeSubmit)
    }

    this.bindTinyMceDialogFocusFix()

    this.loadTinyMce().then(() => {
      this.initializeEditors()
    }).catch(() => {
      console.error('[LexiqamusSitePageForm] TinyMCE failed to load. Rich text editing is unavailable.')
    })

    if (this.hasMediaModalTarget) {
      // Store a stable reference so disconnect() can remove the exact same listener.
      this._onMediaModalShown = () => this.reloadMedia()
      this.mediaModalTarget.addEventListener('shown.bs.modal', this._onMediaModalShown)
    }
  }

  // Removes form submit listener, media modal listener, TinyMCE focus fix, and all editor instances.
  disconnect() {
    if (this.formElement && this.syncEditorsBeforeSubmit) {
      this.formElement.removeEventListener('submit', this.syncEditorsBeforeSubmit)
    }

    if (this.hasMediaModalTarget && this._onMediaModalShown) {
      this.mediaModalTarget.removeEventListener('shown.bs.modal', this._onMediaModalShown)
    }

    if (this._onTinyMceDialogFocusIn) {
      document.removeEventListener('focusin', this._onTinyMceDialogFocusIn, true)
    }

    if (window.tinymce) {
      this.element.querySelectorAll('.js-site-page-rich-text').forEach((textarea) => {
        const editor = window.tinymce.get(textarea.id)
        if (editor) {
          editor.remove()
        }
      })
    }
  }

  // Flushes all TinyMCE editor content back into their hidden textareas before the form posts.
  syncEditorsBeforeSubmit() {
    if (window.tinymce) {
      window.tinymce.triggerSave()
    }
  }

  // Opens media library in editor mode — image will be inserted at TinyMCE cursor position.
  openEditorPicker() {
    this.mediaMode      = 'editor'
    this.heroLanguageId = null
    this.showMediaModal()
  }

  // Opens media library in hero mode for the language tab that triggered the action.
  openHeroPicker(event) {
    this.mediaMode      = 'hero'
    this.heroLanguageId = event.currentTarget.dataset.languageId
    this.showMediaModal()
  }

  // Clears the hero image field and resets the preview to the placeholder for the given language tab.
  clearHeroImage(event) {
    const languageId  = event.currentTarget.dataset.languageId
    const field       = this.heroField(languageId)
    const preview     = this.heroPreview(languageId)
    const clearButton = event.currentTarget

    if (field)       { field.value = '' }
    if (preview)     { preview.src = this.undefinedHeroImageUrlValue }
    if (clearButton) { clearButton.classList.add('d-none') }
  }

  // Falls back to the placeholder when a hero <img> fails to load (broken URL or missing blob).
  // Replaces the inline onerror handler to avoid CSP violations and JS injection risks.
  handleHeroImageError(event) {
    event.currentTarget.src = this.undefinedHeroImageUrlValue
  }

  // Handles file input change from the media library upload button.
  // Uploads all selected files; successful items are reflected even when some fail.
  // Auto-applies the last successfully uploaded item as hero if in hero mode.
  handleLibraryUpload(event) {
    const files = Array.from(event.target.files || [])
    if (!files.length) { return }

    this.setSpinnerVisible(true)

    Promise.allSettled(files.map((file) => this.uploadFileItem(file)))
      .then((results) => {
        const succeeded = results
          .filter((r) => r.status === 'fulfilled')
          .map((r) => r.value)
        const failCount = results.length - succeeded.length

        this.reloadMedia()

        if (failCount > 0) {
          window.alert(`${failCount} image(s) failed to upload.`)
        }

        if (this.mediaMode === 'hero' && succeeded.length > 0) {
          const item       = succeeded[succeeded.length - 1]
          const languageId = this.heroLanguageId || this.activeLanguageId()
          this.applyHeroImage(languageId, item.stored_value, item.small_url)
          this.hideMediaModal()
        }
      })
      .finally(() => {
        this.setSpinnerVisible(false)
        event.target.value = ''
      })
  }

  // Handles the per-language direct hero upload input (outside the media modal).
  handleHeroDirectUpload(event) {
    const file = event.target.files?.[0]
    if (!file) { return }

    const languageId    = event.currentTarget.dataset.languageId
    this.heroLanguageId = languageId
    this.setSpinnerVisible(true)

    this.uploadFileItem(file)
      .then((item) => {
        this.applyHeroImage(languageId, item.stored_value, item.small_url)
      })
      .catch(() => window.alert('Hero image upload failed.'))
      .finally(() => {
        this.setSpinnerVisible(false)
        event.target.value = ''
      })
  }

  // Applies the clicked thumbnail as the hero image for the current language tab and closes the modal.
  selectHeroImageFromThumb(event) {
    event.stopPropagation()

    const thumb = event.currentTarget.closest('.js-site-page-media-thumb')
    if (!thumb) { return }

    const languageId = this.heroLanguageId || this.activeLanguageId()
    this.applyHeroImage(languageId, thumb.dataset.storedValue, thumb.dataset.smallUrl)
  }

  // Inserts the clicked thumbnail's image URL into the active TinyMCE editor and closes the modal.
  insertImageFromThumb(event) {
    event.stopPropagation()

    const thumb = event.currentTarget.closest('.js-site-page-media-thumb')
    if (!thumb) { return }

    this.insertIntoEditor(thumb.dataset.originalUrl)
    this.hideMediaModal()
  }

  // Confirms and deletes an Active Storage blob, then reloads the media grid.
  removeImageFromThumb(event) {
    event.stopPropagation()

    const thumb = event.currentTarget.closest('.js-site-page-media-thumb')
    if (!thumb) { return }

    if (!window.confirm('Delete this image?')) { return }

    const removeUrl = this.removeMediaUrlValue.replace('MEDIA_ID', thumb.dataset.mediaId)

    fetch(removeUrl, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': this.csrfToken() },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) { throw new Error('Delete failed') }
        this.reloadMedia()
      })
      .catch(() => window.alert('Could not delete image.'))
  }

  // Opens the full-size preview modal for a thumbnail.
  previewImageFromThumb(event) {
    const thumb = event.currentTarget.closest('.js-site-page-media-thumb')
    if (!thumb || !this.hasPreviewImageTarget) { return }

    this.previewImageTarget.src       = thumb.dataset.originalUrl
    this.previewCaptionTarget.textContent = thumb.dataset.basename
    this.previewModalInstance = this.previewModalInstance || new window.bootstrap.Modal(this.previewModalTarget)
    this.previewModalInstance.show()
  }

  // Bootstrap modals steal focus from TinyMCE dialogs; keep focus inside tox-dialog.
  bindTinyMceDialogFocusFix() {
    this._onTinyMceDialogFocusIn = (event) => {
      if (event.target.closest?.('.tox-dialog')) {
        event.stopImmediatePropagation()
      }
    }

    document.addEventListener('focusin', this._onTinyMceDialogFocusIn, true)
  }

  // Lazily loads self-hosted TinyMCE 5 from jsDelivr.
  loadTinyMce() {
    if (window.tinymce) { return Promise.resolve() }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = `${TINYMCE_SELF_HOSTED_BASE_URL}/tinymce.min.js`
      script.referrerPolicy = 'origin'
      script.onload         = () => resolve()
      script.onerror        = () => reject(new Error('TinyMCE script failed to load'))
      document.head.appendChild(script)
    })
  }

  // Removes any stale TinyMCE instances then re-initialises one editor per language textarea.
  initializeEditors() {
    if (!window.tinymce) { return }

    // Remove stale instances left over from Turbo navigations or hot reloads.
    // Snapshot the editors list first — removing items while iterating the live array
    // can skip entries if tinymce.remove mutates the array before the loop advances.
    const staleEditors = Array.from(window.tinymce.editors)
    staleEditors.forEach((editor) => {
      try {
        window.tinymce.remove(editor)
      } catch (error) {
        // ignore stale editor instances
      }
    })

    this.element.querySelectorAll('.js-site-page-rich-text').forEach((textarea) => {
      // TinyMCE requires a stable DOM id to bind to.
      if (!textarea.id) {
        textarea.id = `site-page-editor-${textarea.dataset.languageId}`
      }

      window.tinymce.init(this.tinymceEditorConfig(textarea))
    })
  }

  // legacyoutput / ottpron / ottword intentionally omitted (CPG-194): legacyoutput emits
  // font-size: 0 with fontsize_formats; ottpron/ottword are not used on CMS pages.
  tinymceEditorConfig(textarea) {
    return {
      target: textarea,
      base_url: TINYMCE_SELF_HOSTED_BASE_URL,
      suffix: '.min',
      plugins: [
        'code', 'visualblocks', 'searchreplace', 'wordcount', 'paste', 'autoresize',
        'noneditable', 'link', 'autolink', 'preview', 'table', 'lists', 'textcolor',
        'image',
      ].join(' '),
      toolbar: [
        'code',
        'cut copy paste',
        'bold italic underline',
        'alignleft aligncenter alignright alignjustify',
        'removeformat',
        'styleselect fontselect fontsizeselect',
        'link unlink image',
        'forecolor backcolor',
        'h1 h2 h3 h4 h5 h6',
        'table tabledelete tableprops tablerowprops tablecellprops',
        'tableinsertrowbefore tableinsertrowafter tabledeleterow',
        'tableinsertcolbefore tableinsertcolafter tabledeletecol',
      ].join(' | '),
      height: 200,
      menubar: 'edit format view',
      image_description: true,
      image_title: false,
      image_advtab: false,
      image_dimensions: true,
      menu: {
        edit: { title: 'Edit', items: 'undo redo | cut copy paste | selectall | searchreplace' },
        format: { title: 'Format', items: 'strikethrough superscript subscript | formats fontformats blockformats' },
      },
      schema: 'html5',
      element_format: 'html',
      verify_html: false,
      keep_styles: true,
      inline_styles: true,
      branding: false,
      // Intentionally permissive in the editor: lab-imported HTML + Code view must round-trip
      // without TinyMCE stripping tags/attrs. Public safety is NOT enforced here —
      // `lexiqamus_site_page_body_html` + SitePageRichText::ALLOWED_* scrub on render.
      // Aligning valid_elements with ALLOWED_* would break lab fidelity (@media styles,
      // form/pricing markup, custom attrs). Keep editor open; keep server sanitizer strict.
      valid_elements: '*[*]',
      valid_children: '+body[style],+div[style],+span[style]',
      custom_elements: 'style',
      paste_retain_style_properties: 'all',
      paste_webkit_styles: 'all',
      convert_urls: false,
      entity_encoding: 'raw',
      min_height: 100,
      max_height: window.innerHeight - 410,
      fontsize_formats: '8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 25pt 26pt 27pt 28pt 29pt 30pt 31pt 32pt 33pt 34pt 35pt 36pt 48pt',
      content_style: this.tinymceRichTextContentStyle(),
      images_upload_handler: (blobInfo) => this.uploadInlineImage(blobInfo),
      setup: (editor) => this.bindTinyMceStylePreservation(editor),
    }
  }

  // Soft defaults for the iframe — mirrors public .lexiqamus-site-page-rich-text.
  tinymceRichTextContentStyle() {
    return [
      `body { color: ${TINYMCE_BODY_TEXT_COLOR}; font-size: 1rem; line-height: 1.7; font-family: Arial, Helvetica, sans-serif; }`,
      'p { margin: 0 0 1rem; }',
      'h1, h2, h3, h4, h5, h6 { margin-top: 1.5rem; margin-bottom: 0.75rem; }',
      'img { max-width: 100%; height: auto; }',
      'ul, ol { margin: 0 0 1rem; padding-left: 1.5rem; }',
      'table { border-collapse: collapse; width: auto; max-width: 100%; margin-bottom: 1rem; }',
      'table td, table th { padding: 8px; vertical-align: top; }',
      `table td:not([style*='border']), table th:not([style*='border']) { border: 1px solid ${TINYMCE_TABLE_CELL_BORDER}; }`,
      `blockquote { margin: 1rem 0; padding-left: 1rem; border-left: 3px solid ${TINYMCE_BLOCKQUOTE_BORDER}; }`,
      `.mceNonEditable { background-color: ${TINYMCE_NON_EDITABLE_BG}; padding: 0 2px; }`,
    ].join(' ')
  }

  // Apply body <style> tags inside the iframe head so Visual matches Code / saved HTML.
  // Admin-only: styles are injected unsanitized for WYSIWYG. Public render runs
  // sanitize_style_tag_css — Visual may look correct while public strips/drops CSS.
  bindTinyMceStylePreservation(editor) {
    const applyEmbeddedStylesInIframe = () => {
      const doc  = editor.getDoc()
      const body = editor.getBody()
      if (!doc || !body) return

      doc.querySelectorAll(`style[${TINYMCE_CMS_EMBEDDED_STYLE_ATTR}="${TINYMCE_CMS_EMBEDDED_STYLE_VALUE}"]`).forEach((node) => node.remove())

      body.querySelectorAll('style').forEach((styleEl) => {
        const injected = doc.createElement('style')
        injected.setAttribute('type', 'text/css')
        injected.setAttribute(TINYMCE_CMS_EMBEDDED_STYLE_ATTR, TINYMCE_CMS_EMBEDDED_STYLE_VALUE)
        injected.textContent = styleEl.textContent || ''
        doc.head.appendChild(injected)
      })
    }

    editor.on('init', applyEmbeddedStylesInIframe)
    editor.on('SetContent', applyEmbeddedStylesInIframe)
  }

  // Fetches the media library JSON and re-renders the thumbnail grid.
  reloadMedia() {
    if (!this.hasLibraryContainerTarget) { return }

    this.setSpinnerVisible(true)

    fetch(this.loadImageDataUrlValue, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((items) => { this.renderMediaGrid(items) })
      .catch(() => {
        this.libraryContainerTarget.innerHTML = '<p class="text-muted mb-0">Could not load media library.</p>'
      })
      .finally(() => this.setSpinnerVisible(false))
  }

  // Renders the media thumbnail grid or an empty-state message when no images exist yet.
  renderMediaGrid(items) {
    if (!items.length) {
      this.libraryContainerTarget.innerHTML = '<p class="text-muted mb-0">No images yet. Upload to get started.</p>'
      return
    }

    this.libraryContainerTarget.innerHTML = ''
    items.forEach((item) => {
      this.libraryContainerTarget.appendChild(this.mediaThumbElement(item))
    })
  }

  // Builds a DOM element for a single media thumbnail.
  // Uses DOM construction instead of innerHTML interpolation to prevent XSS.
  // The primary click action switches between hero-select and editor-insert based on mediaMode.
  mediaThumbElement(item) {
    const selectAction = this.mediaMode === 'hero'
      ? 'click->lexiqamus-site-page-form#selectHeroImageFromThumb'
      : 'click->lexiqamus-site-page-form#insertImageFromThumb'

    const thumb = document.createElement('div')
    thumb.className = 'lexiqamus-site-page-media-thumb js-site-page-media-thumb'
    thumb.dataset.mediaId     = item.id
    thumb.dataset.basename    = item.basename
    thumb.dataset.storedValue = item.stored_value
    thumb.dataset.smallUrl    = item.small_url
    thumb.dataset.originalUrl = item.original_url

    const img = document.createElement('img')
    img.src       = item.small_url
    img.alt       = item.basename
    img.className = 'img-thumbnail lexiqamus-site-page-media-thumb-image'
    img.dataset.action = 'click->lexiqamus-site-page-form#previewImageFromThumb'

    const title = document.createElement('div')
    title.className   = 'lexiqamus-site-page-media-thumb-title'
    title.textContent = item.basename

    const actions = document.createElement('div')
    actions.className = 'lexiqamus-site-page-media-thumb-actions'

    const deleteBtn = document.createElement('button')
    deleteBtn.type          = 'button'
    deleteBtn.className     = 'btn btn-sm btn-danger'
    deleteBtn.dataset.action = 'click->lexiqamus-site-page-form#removeImageFromThumb'
    deleteBtn.title         = 'Delete'
    deleteBtn.setAttribute('aria-label', 'Delete image')
    deleteBtn.textContent   = '×'

    const selectBtn = document.createElement('button')
    selectBtn.type          = 'button'
    selectBtn.className     = 'btn btn-sm btn-success'
    selectBtn.dataset.action = selectAction
    selectBtn.title         = 'Select'
    selectBtn.setAttribute('aria-label', 'Select image')
    selectBtn.textContent   = '✓'

    actions.appendChild(deleteBtn)
    actions.appendChild(selectBtn)
    thumb.appendChild(img)
    thumb.appendChild(title)
    thumb.appendChild(actions)

    return thumb
  }

  // Writes the stored_value path into the hidden hero field and updates the preview image.
  // Also reveals the clear button so admins can remove the hero image later.
  applyHeroImage(languageId, storedValue, previewUrl) {
    const field       = this.heroField(languageId)
    const preview     = this.heroPreview(languageId)
    const clearButton = this.element.querySelector(`.js-site-page-hero-clear[data-language-id="${languageId}"]`)

    if (field)       { field.value = storedValue }
    if (preview)     { preview.src = previewUrl }
    if (clearButton) { clearButton.classList.remove('d-none') }

    this.hideMediaModal()
  }

  // Inserts an <img> tag at the TinyMCE cursor position and opens the image dialog so admins
  // can enter alt text (Alternative description) before saving.
  insertIntoEditor(src) {
    const editor = this.activeEditor()
    if (!editor) {
      window.alert('Open a language tab with page content before inserting a picture.')
      return
    }

    // Escape src through a real DOM attribute assignment — neutralises any injected attributes
    // (e.g. `" onload="...`) that would otherwise break out of the src string in insertContent.
    const safeImg = document.createElement('img')
    safeImg.src = src
    safeImg.alt = ''
    safeImg.id  = 'mce-new-inserted-image'

    editor.insertContent(safeImg.outerHTML)
    editor.focus()

    // Retrieve the inserted image, remove the temporary ID, select it, and open TinyMCE's
    // image properties dialog so the user can set alt text, dimensions, and other attributes.
    const inserted = editor.dom.get('mce-new-inserted-image')
    if (!inserted) { return }

    editor.dom.setAttrib(inserted, 'id', null)
    editor.selection.select(inserted)
    editor.execCommand('mceImage')
  }

  // TinyMCE images_upload_handler: uploads a blob from a paste/drag and returns the stored URL.
  async uploadInlineImage(blobInfo) {
    const item = await this.uploadFileItem(blobInfo.blob())
    return item.original_url
  }

  // Posts a single file to the upload endpoint and resolves with the media item JSON.
  // Throws on non-2xx responses and on non-JSON bodies (e.g. server error pages).
  async uploadFileItem(file) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(this.uploadUrlValue, {
      method: 'POST',
      body: formData,
      headers: { 'X-CSRF-Token': this.csrfToken() },
      credentials: 'same-origin',
    })

    if (!response.ok) { throw new Error('Upload failed') }

    // Parse JSON separately so a non-JSON error body doesn't mask the real HTTP error.
    const payload = await response.json()
    return payload.item
  }

  // Returns the visible language tab pane (Bootstrap toggles .active / .show on the js hook).
  activeLanguageTabPane() {
    return this.element.querySelector('.js-site-page-lang-tab-pane.active.show') ||
           this.element.querySelector('.js-site-page-lang-tab-pane.active')
  }

  // Returns the TinyMCE instance for the textarea inside the currently visible language tab.
  activeEditor() {
    const activePane = this.activeLanguageTabPane()
    if (!activePane) { return null }

    const textarea = activePane.querySelector('.js-site-page-rich-text')
    if (!textarea || !window.tinymce) { return null }

    return window.tinymce.get(textarea.id)
  }

  // Returns the language_id of the currently visible language tab pane.
  activeLanguageId() {
    const activePane = this.activeLanguageTabPane()
    return activePane ? activePane.dataset.languageId : null
  }

  // Finds the hidden hero image path field for a given language tab.
  heroField(languageId) {
    return this.element.querySelector(`.js-site-page-hero-image-field[data-language-id="${languageId}"]`)
  }

  // Finds the hero preview <img> element for a given language tab.
  heroPreview(languageId) {
    return this.element.querySelector(`.js-site-page-hero-preview[data-language-id="${languageId}"]`)
  }

  // Initialises the Bootstrap modal on first show, then displays it.
  showMediaModal() {
    if (!this.hasMediaModalTarget) { return }

    this.mediaModalInstance = this.mediaModalInstance || new window.bootstrap.Modal(this.mediaModalTarget)
    this.mediaModalInstance.show()
  }

  hideMediaModal() {
    if (this.mediaModalInstance) { this.mediaModalInstance.hide() }
  }

  setSpinnerVisible(visible) {
    if (!this.hasSpinnerTarget) { return }

    this.spinnerTarget.classList.toggle('d-none', !visible)
  }

  // Reads the Rails CSRF token from csrf_meta_tags in the layout (same meta tag pattern as search_output_controller).
  // Required on POST upload and DELETE remove — Rails rejects mutating requests without X-CSRF-Token.
  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
